// components/FastenersPanel.js

import React, { useState } from 'react';
import { Box, Button, Typography, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { styled } from '@mui/system';
import fastenersData from './fastenersData';

const ScrollableContent = styled(Box)({
  overflowY: 'auto', // Прокрутка только для содержимого
  flex: 1,
  // Стилизация полосы прокрутки
  '&::-webkit-scrollbar': {
    width: '0.5rem', // 8px
  },
  '&::-webkit-scrollbar-track': {
    background: '#f5f5f5',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#d0d0d0',
    borderRadius: '0.25rem', // 4px
    '&:hover': {
      background: '#b0b0b0',
    },
  },
});

function FastenersPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // Получаем главные категории из fastenersData
  const mainCategories = [...new Set(Object.values(fastenersData).map(fastener => fastener.mainCategory))];

  // Получаем подкатегории для выбранной главной категории
  const getSubCategories = (mainCategory) => {
    const subCategories = [...new Set(
      Object.values(fastenersData)
        .filter(fastener => fastener.mainCategory === mainCategory)
        .map(fastener => fastener.subCategory)
        .filter(sub => sub !== undefined && sub !== null) // Фильтруем undefined/null
    )];
    return subCategories;
  };

  // Получаем крепежи по главной категории (без подкатегории)
  const getFastenersByMainCategory = (mainCategory) => {
    return Object.keys(fastenersData).filter(
      key => fastenersData[key].mainCategory === mainCategory && 
             !fastenersData[key].subCategory
    );
  };

  // Получаем крепежи по главной и подкатегории
  const getFastenersByCategories = (mainCategory, subCategory) => {
    return Object.keys(fastenersData).filter(
      key => fastenersData[key].mainCategory === mainCategory && 
             fastenersData[key].subCategory === subCategory
    );
  };

  return (
    <Box
      sx={{
        width: '13.75rem',
        padding: '1rem',
        boxSizing: 'border-box',
        borderLeft: '0.0625rem solid #e0e0e0',
        borderTop: '0.0625rem solid #e0e0e0', // Разделительная линия
        // Если открыто - фиксированная высота с прокруткой
        ...(isOpen ? {
          maxHeight: '50vh', // Максимум 50% высоты экрана
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        } : {
          height: 'auto',
        }),
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: '#212121', fontWeight: 600 }}>
        Крепежи
      </Typography>
      <Button
        variant="contained"
        fullWidth
        sx={{
          backgroundColor: isOpen ? '#d32f2f' : '#f5f5f5',
          color: isOpen ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: isOpen ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          mb: 1,
          '&:hover': {
            backgroundColor: isOpen ? '#c62828' : '#e0e0e0',
            border: isOpen ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'Скрыть крепежи' : 'Показать крепежи'}
      </Button>

      {isOpen && (
        <>
          {/* Кнопка "Назад" - фиксированная */}
          {(selectedMainCategory || selectedSubCategory) && (
            <Button
              variant="text"
              sx={{ mb: 1 }}
              onClick={() => {
                if (selectedSubCategory) {
                  setSelectedSubCategory(null);
                  // Если это прямой переход без подкатегории, возвращаемся к категориям
                  if (selectedSubCategory === '__direct__') {
                    setSelectedMainCategory(null);
                  }
                } else {
                  setSelectedMainCategory(null);
                }
              }}
            >
              {selectedSubCategory === '__direct__' ? 'Назад к категориям' : selectedSubCategory ? 'Назад к подкатегориям' : 'Назад к категориям'}
            </Button>
          )}
          
          {/* Прокручиваемый контент */}
          <ScrollableContent>
            {!selectedMainCategory ? (
              // Показываем список главных категорий
              <List>
                {mainCategories.map((mainCategory) => (
                  <ListItem key={mainCategory} disablePadding>
                    <ListItemButton onClick={() => {
                      const subCategories = getSubCategories(mainCategory);
                      // Если нет подкатегорий, переходим сразу к крепежам
                      if (subCategories.length === 0) {
                        setSelectedMainCategory(mainCategory);
                        setSelectedSubCategory('__direct__'); // Специальный флаг
                      } else {
                        setSelectedMainCategory(mainCategory);
                      }
                    }}>
                      <ListItemText primary={mainCategory} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : !selectedSubCategory ? (
              // Показываем список подкатегорий
              <List>
                {getSubCategories(selectedMainCategory).map((subCategory) => (
                  <ListItem key={subCategory} disablePadding>
                    <ListItemButton onClick={() => setSelectedSubCategory(subCategory)}>
                      <ListItemText primary={subCategory} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              // Показываем крепежи
              <List>
                {(selectedSubCategory === '__direct__' 
                  ? getFastenersByMainCategory(selectedMainCategory)
                  : getFastenersByCategories(selectedMainCategory, selectedSubCategory)
                ).map((fastenerKey) => (
                  <ListItem key={fastenerKey} disablePadding>
                    <ListItemButton
                      draggable
                      style={{ cursor: 'grab' }}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', fastenerKey);
                        e.dataTransfer.setData('fastenerKey', fastenerKey);
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                    >
                      <ListItemText 
                        primary={fastenersData[fastenerKey].displayName}
                        secondary={fastenersData[fastenerKey].description}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </ScrollableContent>
        </>
      )}
    </Box>
  );
}

export default FastenersPanel;
